import { useTranslation } from "react-i18next";

import './DashboardPage.scss'
import { Button } from "antd";
import HTMLReactParser from "html-react-parser";
import { useNavigate } from "react-router-dom";
import { GetFormattedUrl } from "../../../utils/urls";
import { useSessionData } from "../../../Contexts/SessionDataContext";
import {
    TeamOutlined, CalendarOutlined, NotificationOutlined,
    FileTextOutlined, ApartmentOutlined, CreditCardOutlined,
    GiftOutlined, SettingOutlined, UserOutlined, BarChartOutlined, ArrowRightOutlined
} from '@ant-design/icons';

const DashboardgPage = (): JSX.Element => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { sessionData } = useSessionData();

    const goAccounts        = () => navigate(GetFormattedUrl('private/accounts-admin'));
    const goBookings        = () => navigate(GetFormattedUrl('private/bookings-admin'));
    const goPayments        = () => navigate(GetFormattedUrl('private/payments-config'));
    const goRewardsForm     = () => window.open('https://www.beds2brewards.com/solicite-una-demo/', '_blank');
    const goConfiguration   = () => navigate(GetFormattedUrl('private/config/pro-configuration'));
    const goCommunications  = () => navigate(GetFormattedUrl('private/communications'));
    const goAccountAsignation = () => navigate(GetFormattedUrl('private/commissions'));
    const goUsers           = () => navigate(GetFormattedUrl('private/users'));
    const goStatistics      = () => navigate(GetFormattedUrl('private/statistics'));

    const modules = [
        {
            section: 'admin_accounts',
            icon: <TeamOutlined />,
            color: '#4d9fff',
            title: t('pages.dashboard.list-item-1'),
            description: t('pages.dashboard.list-item-1-description'),
            buttonLabel: t('pages.dashboard.list-item-1-button'),
            onClick: goAccounts,
        },
        {
            section: 'bookings',
            icon: <CalendarOutlined />,
            color: '#52c41a',
            title: t('pages.dashboard.list-item-2'),
            description: t('pages.dashboard.list-item-2-description'),
            buttonLabel: t('pages.dashboard.list-item-2-button'),
            onClick: goBookings,
        },
        {
            section: 'news_management',
            icon: <NotificationOutlined />,
            color: '#fa8c16',
            title: t('pages.dashboard.list-item-6'),
            description: t('pages.dashboard.list-item-6-description'),
            buttonLabel: t('pages.dashboard.list-item-6-button'),
            onClick: goCommunications,
        },
        {
            section: 'statistics',
            icon: <BarChartOutlined />,
            color: '#52c41a',
            title: t('pages.dashboard.statistics'),
            description: t('pages.dashboard.statistics-description'),
            buttonLabel: t('pages.dashboard.statistics'),
            onClick: goStatistics,
        },
        {
            section: 'contracts',
            icon: <FileTextOutlined />,
            color: '#722ed1',
            title: t('pages.dashboard.list-item-8'),
            description: t('pages.dashboard.list-item-8-description'),
            buttonLabel: t('pages.dashboard.list-item-8-button'),
            onClick: goAccountAsignation,
        },
        {
            section: 'account_assignment',
            icon: <ApartmentOutlined />,
            color: '#13c2c2',
            title: t('pages.dashboard.list-item-7'),
            description: t('pages.dashboard.list-item-7-description'),
            buttonLabel: t('pages.dashboard.list-item-7-button'),
            onClick: goAccountAsignation,
        },
        {
            section: 'payment_config',
            icon: <CreditCardOutlined />,
            color: '#eb2f96',
            title: t('pages.dashboard.list-item-3'),
            description: t('pages.dashboard.list-item-3-description'),
            buttonLabel: t('pages.dashboard.list-item-3-button'),
            onClick: goPayments,
        },
        {
            section: 'users',
            icon: <UserOutlined />,
            color: '#1890ff',
            title: t('pages.dashboard.list-item-9'),
            description: t('pages.dashboard.list-item-9-description'),
            buttonLabel: t('pages.dashboard.list-item-9-button'),
            onClick: goUsers,
        },
        {
            section: 'configuration',
            icon: <SettingOutlined />,
            color: '#595959',
            title: t('pages.dashboard.list-item-5'),
            description: t('pages.dashboard.list-item-5-description'),
            buttonLabel: t('pages.dashboard.list-item-5-button'),
            onClick: goConfiguration,
        },
    ];

    const showRewards = !sessionData.userData.permissions.some(x => (x.section as any) === 'rewards');

    const featuredSections = ['admin_accounts', 'bookings', 'news_management'];

    const visibleModules = modules.filter(m => {
        return sessionData.userData.permissions.some(x => x.section === m.section);
    });

    const featuredModules = visibleModules.filter(m => featuredSections.includes(m.section));
    const configModules   = visibleModules.filter(m => !featuredSections.includes(m.section));

    return (
        <div className="dashboard-page">
            <div className="dashboard-welcome">
                <h2>{HTMLReactParser(t('pages.dashboard.title'))}</h2>
                <p>{HTMLReactParser(t('pages.dashboard.welcome-message'))}</p>
            </div>

            {/* ── Módulos principales ───────────────────────────────── */}
            {featuredModules.length > 0 && (
                <div className="dashboard-featured">
                    {featuredModules.map((mod, idx) => (
                        <div className="featured-card" key={idx} style={{ '--card-color': mod.color } as React.CSSProperties}>
                            <div className="featured-top-bar" />
                            <div className="featured-icon-wrap">
                                <span className="featured-icon" style={{ color: mod.color }}>{mod.icon}</span>
                            </div>
                            <div className="featured-body">
                                <h3 className="featured-title">{mod.title}</h3>
                                <p className="featured-description">{mod.description}</p>
                            </div>
                            <div className="featured-footer">
                                <Button className="featured-btn" onClick={mod.onClick}>
                                    {mod.buttonLabel}
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Banner Rewards (solo si no tiene el producto) ─────── */}
            {showRewards && (
                <div className="rewards-banner" onClick={goRewardsForm}>
                    <div className="rewards-banner-icon"><GiftOutlined /></div>
                    <div className="rewards-banner-content">
                        <span className="rewards-banner-title">{HTMLReactParser(t('pages.dashboard.list-item-4'))}</span>
                        <span className="rewards-banner-text">{t('pages.dashboard.list-item-4-description-1')}</span>
                    </div>
                    <Button className="rewards-banner-btn" onClick={goRewardsForm}>
                        {HTMLReactParser(t('pages.dashboard.list-item-4-button'))} <ArrowRightOutlined />
                    </Button>
                </div>
            )}

            {/* ── Configuración ─────────────────────────────────────── */}
            {configModules.length > 0 && (
                <>
                    <div className="config-section-label">Configuración</div>
                    <div className="dashboard-config">
                        {configModules.map((mod, idx) => (
                            <div className="config-card" key={idx} style={{ '--card-color': mod.color } as React.CSSProperties} onClick={mod.onClick}>
                                <span className="config-icon" style={{ color: mod.color, background: `${mod.color}18` }}>
                                    {mod.icon}
                                </span>
                                <div className="config-content">
                                    <span className="config-title">{mod.title}</span>
                                    <span className="config-description">{mod.description}</span>
                                </div>
                                <span className="config-arrow">›</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default DashboardgPage;
